import { useEffect, useState } from 'react';
import { hmqApiUrl, isOnPremMode } from '../utils/runtimeConfig';

// localStorage key remembering the last successfully fetched license status.
// A gated on-prem deployment drops its pods out of the Service, so a lapsed
// license usually LOOKS like a network failure from the browser. The cached
// status is what lets the banner tell "license lapsed" apart from an ordinary
// API blip after the endpoint stops answering.
export const LICENSE_STATUS_STORAGE_KEY = 'hermetiq.licenseStatus';
export const LICENSE_POLL_INTERVAL_MS = 5 * 60 * 1000;

// Unauthenticated, on-prem-only endpoint (see bep-nats rest/license_status.go).
// The SaaS deployment does not register it, so the hook never fetches unless
// the dashboard is running in on-prem mode.
const LICENSE_STATUS_PATH = '/api/v1/license/status';

const isLicenseStatus = (value) =>
  Boolean(value) && typeof value === 'object' && typeof value.state === 'string' && value.state !== '';

const readCachedStatus = () => {
  try {
    const raw = window.localStorage.getItem(LICENSE_STATUS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isLicenseStatus(parsed) ? parsed : null;
  } catch (error) {
    return null;
  }
};

const writeCachedStatus = (status) => {
  try {
    window.localStorage.setItem(LICENSE_STATUS_STORAGE_KEY, JSON.stringify(status));
  } catch (error) {
    // Private browsing / storage quota — the in-memory copy still works.
  }
};

/**
 * Polls the on-prem license status endpoint every 5 minutes.
 *
 * Returns { status, fetchFailed }:
 *   - status: the last known license status object (live result when a fetch
 *     has succeeded this session, otherwise the localStorage-cached copy once
 *     a fetch has failed), or null when nothing is known.
 *   - fetchFailed: true when the most recent fetch attempt failed.
 *
 * Never fetches in SaaS mode and never throws into the UI — every failure is
 * absorbed into fetchFailed.
 */
export function useLicenseStatus() {
  const [status, setStatus] = useState(null);
  const [fetchFailed, setFetchFailed] = useState(false);

  useEffect(() => {
    if (!isOnPremMode) return undefined;

    let cancelled = false;

    const fetchStatus = async () => {
      try {
        const response = await fetch(`${hmqApiUrl}${LICENSE_STATUS_PATH}`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) {
          throw new Error(`license status returned HTTP ${response.status}`);
        }
        const body = await response.json();
        if (cancelled) return;
        if (!isLicenseStatus(body)) {
          throw new Error('license status response missing state');
        }
        setStatus(body);
        setFetchFailed(false);
        writeCachedStatus(body);
      } catch (error) {
        if (cancelled) return;
        console.warn('[useLicenseStatus] license status fetch failed:', error?.message || error);
        setFetchFailed(true);
        // Keep the last successful status from this session; fall back to the
        // localStorage copy from a previous session so the banner can decide
        // whether this outage is a lapsed license.
        setStatus((previous) => previous ?? readCachedStatus());
      }
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, LICENSE_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  return { status, fetchFailed };
}
